import React, { useState, useEffect } from 'react'
import { PageHeading } from 'app/layout/PageHeading'
import { Search, ExpansionPanel } from 'app/shared/components'
import { useAnalytics } from 'app/shared/hooks/useAnalytics'
import { fuzzySearch } from 'app/shared/utils'
import { JeffBezosTestimonial } from 'app/layout/Testimonial'

export const classSpecificQuestions = [
  {
    question: 'Can I join a class that has already started?',
    answer:
      'Yes, with caveats.  You can join if you have teacher permission, are confident in the material, or have taken the previous classes a la carte.',
  },
  {
    question: 'What class should I take?',
    answer:
      'How much experience do you have?  If you have never danced before, you should start with our Starting Swing series. If you have some experience, take a look at our curriculum to see where to jump in.',
  },
  {
    question: 'Do I need a partner?',
    answer:
      'No! We try our best to balance classes and will rotate partners throughout the class. It’s a great way to meet new people and learn more.',
  },
  {
    question: 'What do I wear to a class?',
    answer:
      "Wear what's comfortable!  Generally, athletic wear is a good idea.",
  },
  {
    question: 'Can I dance with only my partner?',
    answer:
      'You can dance with whoever you want!  Just let the teachers know beforehand, since we rotate partners by default in our classes.',
  },
  {
    question: 'How long does it take to become proficient?',
    answer:
      "This depends on what you mean by 'proficient'.  If you just want to have fun and enjoy a night of dancing, that can take as little as 30 minutes.  It can take a few months for you to really feel comfortable.  That said, the best way to learn is to get out and dance!",
  },
]

export const faqContent = [
  {
    question: 'What is swing dancing?',
    answer:
      'Swing dance actually refers to many forms of dance, including Lindy Hop, Charleston, East Coast, Balboa, Shag, and more! It includes solo jazz routines in addition to these partnered dances. Swing came about in Harlem in the 1920s and evolved with the music of that time. It gained popularity throughout the big band era and later saw a revival in the ’90s. Swing continues to grow and evolve all over the world today.',
  },
  {
    question: 'What kind of music are we dancing to?',
    answer:
      'Swing is generally danced to jazz music from the 1920s to the 1950s. There are many contemporary bands that are great for dancing as well, and even some local jazz bands here in St. Louis.',
  },
  {
    question: 'Can I swing dance to modern music?',
    answer:
      "You can dance to anything you want!  That said, the more you learn about the dance and the more adept you become, the more you will want to dance to jazz. Dances are created based on the music, not the other way around. Eventually, dancing Lindy Hop to anything other than jazz just doesn't feel right.",
  },
  {
    question: 'What is the age range of people who dance Lindy Hop?',
    answer:
      'Anyone can dance Lindy Hop!  While the majority of people who take our classes are college students and young professionals between 21 and 35, one of the best dancers in STL is 65 years old.  ',
  },
  {
    question: 'What do I wear to a dance?',
    answer:
      "This depends on whether the dance has live music or not.  For a random weekly dance, wear what's comfortable.  This can be anything from jeans and a t-shirt to yoga pants and activewear.  If there is live music, it's considered respectful to the band to dress up a bit (not to mention fun!).  Shoot for business casual.",
  },
  {
    question: 'Where can I go to social dance?',
    answer:
      'Come to Wednesday Night Swing, our weekly social dance. It runs every Wednesday from 8:30 PM to 10:30 PM at The Link Auditorium, and it is included with every class we teach.',
  },
  ...classSpecificQuestions,
]

export const FaqList = ({ faqs }: any) => {
  const [term, setTerm] = useState('')
  return (
    <section>
      <div className='p-2 bg-gray-200'>
        <Search onSearch={setTerm} />
      </div>
      <ul className='p-4 space-y-2'>
        {fuzzySearch(classSpecificQuestions, term).map(
          ({ question, answer }, i) => (
            <ExpansionPanel title={question}>
              <div className='p-2'>{answer}</div>
            </ExpansionPanel>
          ),
        )}
      </ul>
    </section>
  )
}
export const FaqModalContent = () => {
  const [term, setTerm] = useState('')
  const { pageViewEvent } = useAnalytics()

  useEffect(() => {
    pageViewEvent('Faq Modal')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <section>
      <div className='p-2 bg-gray-200'>
        <Search onSearch={setTerm} />
      </div>
      <ul className='p-4 space-y-2'>
        {fuzzySearch(classSpecificQuestions, term).map(
          ({ question, answer }, i) => (
            <ExpansionPanel title={question}>
              <div className='p-2'>{answer}</div>
            </ExpansionPanel>
          ),
        )}
      </ul>
    </section>
  )
}

export const FaqPage = () => {
  const [term, setTerm] = useState('')
  const { pageViewEvent } = useAnalytics()

  useEffect(() => {
    pageViewEvent('Faq')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <section className='relative z-0'>
      <PageHeading title='FAQ' image='getdown/kevin-and-juliane-2.jpg' position='50% 60%' />

      <div className='max-w-6xl px-4 py-12 mx-auto sm:px-6 sm:py-16'>
        <div className='max-w-xl'>
          <Search onSearch={setTerm} />
        </div>

        <ul className='mt-10 border-b border-rule'>
          {fuzzySearch(faqContent, term).map(({ question, answer }) => (
            <li
              key={question}
              className='py-8 border-t border-rule lg:grid lg:grid-cols-12 lg:gap-12'>
              <h2 className='h3 lg:col-span-5'>
                {question}
              </h2>
              <p className='mt-3 body lg:mt-0 lg:col-span-7'>
                {answer}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <JeffBezosTestimonial />
    </section>
  )
}

export default FaqPage
